import { apiGet, apiPost } from './client';
import type { OperationResult } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Skill history — versioned audit trail of ai.AiSkill content edits.
//
// Each row holds the body that WAS REPLACED on a save — the current body
// always lives on ai.AiSkill. The drawer loads list rows (no body) up-front,
// then fetches one detail at a time for diff rendering.
//
// Restore copies a prior snapshot back onto the live ai.AiSkill row. The
// standard Update path records the now-prior body in history first, so a
// restore is itself auditable + reversible.
// ─────────────────────────────────────────────────────────────────────────────

/** Summary row — `instructions` body intentionally NOT included to keep the list payload small. */
export interface SkillHistoryRow {
  id: number;
  aiSkillId: number;
  name?: string | null;
  category?: string | null;
  version?: string | null;
  /** ISO string. */
  changedDate: string;
  /** Employee id of the editor (0 = system). */
  changedBy: number;
  /** Resolved DisplayName when available — null if backend doesn't surface it. */
  changedByName?: string | null;
  charCount: number;
  instructionsHash?: string | null;
}

/** Detail row — same as summary plus the full prior body for diff rendering. */
export interface SkillHistoryDetail extends SkillHistoryRow {
  description?: string | null;
  instructions?: string | null;
}

export interface SkillHistoryPage {
  rowCount: number;
  pageNo: number;
  pageSize: number;
  data: SkillHistoryRow[];
}

export async function listSkillHistory(
  skillId: number,
  pageNo = 1,
  pageSize = 50,
): Promise<SkillHistoryPage> {
  const wrapped = await apiGet<OperationResult<SkillHistoryPage>>(
    `/api/AiSkill/History/${skillId}`,
    { pageNo, pageSize },
  );
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || 'We couldn’t load the version history. Please try again in a moment.');
  }
  return wrapped.data;
}

export async function getSkillHistoryDetail(
  skillId: number,
  historyId: number,
): Promise<SkillHistoryDetail> {
  const wrapped = await apiGet<OperationResult<SkillHistoryDetail>>(
    `/api/AiSkill/History/${skillId}/${historyId}`,
  );
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || 'We couldn’t load that version.');
  }
  return wrapped.data;
}

export interface RestoreSkillResponse {
  skillId: number;
  instructions: string;
  description?: string | null;
  category?: string | null;
  version?: string | null;
  updatedDate: string;
}

export async function restoreSkillHistory(
  skillId: number,
  historyId: number,
): Promise<RestoreSkillResponse> {
  const wrapped = await apiPost<OperationResult<RestoreSkillResponse>>(
    `/api/AiSkill/History/${skillId}/${historyId}/Restore`,
    {},
  );
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || 'We couldn’t switch to that version. Please try again.');
  }
  return wrapped.data;
}
